import { freeze } from 'immer';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import { CHART_TYPES } from '../../constants/chartTypes';
import { z } from 'zod';
import { IDisplayedMeasure } from '../../interfaces';
import { YAxisPosition } from '../../interfaces';

export enum CentralChartSettingAccordionType {
  MeasureAssignments = 'MEASURE_ASSIGNMENTS',
  LeftYAxisAssignments = 'LEFT_Y_AXIS_ASSIGNMENTS',
  RightYAxisAssignments = 'RIGHT_Y_AXIS_ASSIGNMENTS',
  FunnelAssignments = 'FUNNEL_ASSIGNMENTS',
}

export enum AxisOptionType {
  Visibility = 'VISIBILITY',
  Format = 'FORMAT',
  Bounds = 'BOUNDS',
}

export interface ICentralChartSettingOptions {
  [CentralChartSettingAccordionType.MeasureAssignments]?: boolean;
  [CentralChartSettingAccordionType.LeftYAxisAssignments]?: Partial<
    Record<AxisOptionType, boolean>
  >;
  [CentralChartSettingAccordionType.RightYAxisAssignments]?: Partial<
    Record<AxisOptionType, boolean>
  >;
  [CentralChartSettingAccordionType.FunnelAssignments]?: boolean;
}

export type ICentralChartSettingTypeSettings =
  | Partial<Record<CHART_TYPES, ICentralChartSettingOptions>>
  | undefined;

export type ICentralChartSettingVisibleConfig = Partial<
  Record<QueryType, ICentralChartSettingTypeSettings>
>;

const allEnabledAxisOptions = freeze(
  {
    [AxisOptionType.Visibility]: true,
    [AxisOptionType.Format]: true,
    [AxisOptionType.Bounds]: true,
  },
  true,
);

export const CENTRAL_CHART_SETTING_VISIBLE_CONFIG: ICentralChartSettingVisibleConfig =
  freeze(
    {
      [QueryType.Expression]: {
        [CHART_TYPES.LINE_CHART]: {
          [CentralChartSettingAccordionType.MeasureAssignments]: true,
          [CentralChartSettingAccordionType.LeftYAxisAssignments]:
            allEnabledAxisOptions,
          [CentralChartSettingAccordionType.RightYAxisAssignments]:
            allEnabledAxisOptions,
        },
        [CHART_TYPES.BAR_CHART]: {
          [CentralChartSettingAccordionType.MeasureAssignments]: true,
          [CentralChartSettingAccordionType.LeftYAxisAssignments]:
            allEnabledAxisOptions,
          [CentralChartSettingAccordionType.RightYAxisAssignments]:
            allEnabledAxisOptions,
        },
        [CHART_TYPES.STACKED_COLUMN_CHART]: {
          [CentralChartSettingAccordionType.LeftYAxisAssignments]:
            allEnabledAxisOptions,
        },
        [CHART_TYPES.PERCENT_STACKED_AREA_CHART]: {
          [CentralChartSettingAccordionType.LeftYAxisAssignments]:
            allEnabledAxisOptions,
        },
      },
      [QueryType.Funnel]: {
        [CHART_TYPES.FUNNEL_CHART]: {
          [CentralChartSettingAccordionType.FunnelAssignments]: true,
          [CentralChartSettingAccordionType.LeftYAxisAssignments]: {
            [AxisOptionType.Visibility]: true,
          },
        },
        [CHART_TYPES.COMPARATIVE_LINE_CHART]: {
          [CentralChartSettingAccordionType.LeftYAxisAssignments]: {
            [AxisOptionType.Visibility]: true,
            [AxisOptionType.Bounds]: true,
          },
        },
      },
      [QueryType.Retention]: {
        [CHART_TYPES.RETENTION_LINE]: {
          [CentralChartSettingAccordionType.LeftYAxisAssignments]: {
            [AxisOptionType.Visibility]: true,
            [AxisOptionType.Bounds]: true,
          },
        },
        [CHART_TYPES.RETENTION_BAR]: {
          [CentralChartSettingAccordionType.LeftYAxisAssignments]: {
            [AxisOptionType.Visibility]: true,
            [AxisOptionType.Bounds]: true,
          },
        },
      },
    },
    true,
  );

// this is the special case for expanded bar chart which don't support dual y-axis yet
const EXPANDED_BAR_CHART_CONFIG: ICentralChartSettingOptions = freeze(
  {
    [CentralChartSettingAccordionType.LeftYAxisAssignments]: {
      [AxisOptionType.Visibility]: true,
      [AxisOptionType.Bounds]: true,
    },
  },
  true,
);

export const isCentralChartSettingVisible = ({
  queryType,
  chartType,
  singleDayLabel,
}: {
  queryType: QueryType;
  chartType: CHART_TYPES;
  singleDayLabel?: string;
}) => {
  const chartSettingVisibleConfig =
    CENTRAL_CHART_SETTING_VISIBLE_CONFIG[queryType]?.[chartType];
  if (!chartSettingVisibleConfig) {
    return null;
  }

  if (chartType === CHART_TYPES.BAR_CHART && singleDayLabel) {
    return EXPANDED_BAR_CHART_CONFIG;
  }

  return chartSettingVisibleConfig;
};

export const zodCentralChartSettingAccordionType = z.nativeEnum(
  CentralChartSettingAccordionType,
);

const Y_AXIS_ASSIGNMENT_ACCORDION_TYPE = [
  CentralChartSettingAccordionType.LeftYAxisAssignments,
  CentralChartSettingAccordionType.RightYAxisAssignments,
];
/**
 * Check if the assignment is visible for the given accordion type and measureSettings
 * @returns true if the assignment is visible, false otherwise
 */
export const checkIsAxisAssignmentVisible = ({
  queryType,
  chartType,
  singleDayLabel,
  displayedMeasures,
  accordionType,
}: {
  queryType: QueryType;
  chartType: CHART_TYPES;
  singleDayLabel?: string;
  displayedMeasures: IDisplayedMeasure[];
  accordionType: CentralChartSettingAccordionType;
}): boolean => {
  const chartSettingVisibleConfig =
    CENTRAL_CHART_SETTING_VISIBLE_CONFIG[queryType]?.[chartType];

  // if the default config doesn't have the right y-axis assignment, it means the chart type doesn't support dual y-axis
  // always return true for the YAxis assignments visibility.
  if (
    singleDayLabel ||
    !chartSettingVisibleConfig ||
    // if current ChartType don't support dual axis, return true
    !chartSettingVisibleConfig[
      CentralChartSettingAccordionType.RightYAxisAssignments
    ] ||
    !Y_AXIS_ASSIGNMENT_ACCORDION_TYPE.includes(accordionType)
  ) {
    return true;
  }

  // display axis only if the measure is visible and belongs to the axis
  const visibleAxes = [
    ...new Set(
      displayedMeasures
        .filter((measure) => measure.isVisible)
        .map((measure) => measure.yAxisPosition),
    ),
  ];

  const axisPosition =
    accordionType === CentralChartSettingAccordionType.LeftYAxisAssignments
      ? YAxisPosition.LEFT
      : YAxisPosition.RIGHT;
  return visibleAxes.includes(axisPosition);
};
